import React from 'react';
import styled, { keyframes } from 'styled-components';
import { CrudStyles } from '../shared/Crud';
import { isValidEmail } from '../../utils/validation';

const { Button, SectionTitle } = CrudStyles;

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

/** Mesmo padrão de `RolesSection`: afasta do bloco anterior (ex.: permissões). */
const Section = styled.div`
    margin-top: 24px;
    animation: ${fadeIn} 0.4s ease-out;
`;

const Intro = styled.p`
    margin: 0 0 16px;
    font-size: 14px;
    line-height: 1.55;
    color: ${({ theme }) => theme.colors.gray[900]};
    opacity: 0.8;
    max-width: 56ch;
`;

const WarningBox = styled.div`
    margin-bottom: 16px;
    padding: 12px 14px;
    border-radius: 8px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    font-size: 13px;
    line-height: 1.45;
    color: #92400e;
`;

const ActionsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;

    & > button {
        min-height: 40px;
        min-width: min(100%, 168px);
        justify-content: center;
    }

    @media (max-width: 480px) {
        flex-direction: column;
        align-items: stretch;

        & > button {
            width: 100%;
            min-width: unset;
        }
    }
`;

const Footnote = styled.p`
    margin: 16px 0 0;
    padding-top: 16px;
    border-top: 1px solid ${({ theme }) => theme.colors.gray[200]};
    font-size: 12px;
    line-height: 1.5;
    color: ${({ theme }) => theme.colors.gray[900]};
    opacity: 0.55;
`;

/**
 * Controle de bloqueio/desbloqueio de login (empresa de transporte, edição de colaborador).
 * Títulos e separação alinhados a `SectionTitle` (dados pessoais) e `RolesSection` (permissões).
 */
export default function SystemAccessSection({ email, loadingAction, onBlock, onUnblock }) {
    const normalized = String(email || '').trim();
    const canAct = normalized.length > 0 && isValidEmail(normalized);
    const busy = loadingAction != null;

    return (
        <Section role="region" aria-labelledby="system-access-heading" aria-busy={busy}>
            <SectionTitle id="system-access-heading">Acesso ao sistema</SectionTitle>
            <Intro>
                Controle se este colaborador pode entrar no aplicativo. A alteração vale para a conta de usuário
                associada ao e-mail informado em <strong>Dados Pessoais</strong>.
            </Intro>

            {!canAct && (
                <WarningBox role="status">
                    Informe um e-mail válido na seção <strong>Dados Pessoais</strong> para bloquear ou desbloquear o
                    acesso.
                </WarningBox>
            )}

            <ActionsRow>
                <Button
                    type="button"
                    variant="outline"
                    disabled={!canAct || busy}
                    onClick={onUnblock}
                    title="Restaura a possibilidade de login com este e-mail"
                >
                    {loadingAction === 'unblock' ? 'Desbloqueando…' : 'Desbloquear acesso'}
                </Button>
                <Button
                    type="button"
                    variant="danger"
                    disabled={!canAct || busy}
                    onClick={onBlock}
                    title="Impede login com este e-mail até ser desbloqueado"
                >
                    {loadingAction === 'block' ? 'Bloqueando…' : 'Bloquear acesso'}
                </Button>
            </ActionsRow>

            <Footnote>
                Bloquear não remove as permissões do colaborador — apenas impede o login até você desbloquear
                novamente.
            </Footnote>
        </Section>
    );
}
